import React from 'react';
import { InnerButton } from '@/modules/InnerButton';
import { ViewMode } from '@/shared/types/datepicker';
import { NavigationButton } from '@/modules';
import { useCalendar } from '../hooks/useCalendar';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';

interface MultipleCalendarsHeaderProps {
  calendarDates: Date[];
  viewModes: ViewMode[]; // Array de viewModes para cada calendario
  onNavigate: (direction: 'prev' | 'next') => void;
  onToggleViewMode: (calendarIndex: number) => void; // Función que recibe el índice del calendario
  onGoToToday?: () => void;
  monthNames?: string[];
  yearRange?: number;
  renderHeader?: (props: {
    currentDate: Date;
    viewMode: ViewMode;
    toggleViewMode: () => void;
    navigate: (direction: 'prev' | 'next') => void;
  }) => React.ReactNode;
}

export const MultipleCalendarsHeader: React.FC<MultipleCalendarsHeaderProps> = ({
  calendarDates,
  viewModes,
  onNavigate,
  onToggleViewMode,
  onGoToToday,
  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  yearRange = 12,
  renderHeader,
}) => {
  const { getYearRange } = useCalendar(
    calendarDates[0],
    () => {},
    () => {}
  );

  const handleNavigate = (direction: 'prev' | 'next') => {
    onNavigate(direction);
  };

  const formatDate = (date: Date, viewMode: ViewMode) => {
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    switch (viewMode) {
      case 'years':
        // Para años, mostrar el rango completo
        const years = getYearRange(year, yearRange);
        const startYear = years[0];
        const endYear = years[years.length - 1];
        return `${startYear} - ${endYear}`;
      case 'months':
        return `${year}`;
      case 'days':
      default:
        return `${month} ${year}`;
    }
  };

  // Si hay un renderHeader personalizado, usarlo
  if (renderHeader && calendarDates.length > 1) {
    return (
      <div className="ui-border-b ui-border-neutral-100 ui-p-3">
        {renderHeader({
          currentDate: calendarDates[0],
          viewMode: viewModes[0],
          toggleViewMode: () => onToggleViewMode(0),
          navigate: handleNavigate,
        })}
      </div>
    );
  }

  return (
    <div className="ui-border-b ui-border-neutral-100 ui-p-3">
      <div className="ui-relative ui-flex ui-w-full ui-items-center ui-justify-between">
        {/* Botón izquierdo */}
        <NavigationButton direction="prev" onClick={() => onNavigate('prev')} />

        {/* Títulos de los calendarios */}
        <div className="ui-absolute ui-flex ui-w-full ui-flex-1 ui-items-center ui-justify-center ui-gap-2 ui-px-2 md:ui-px-0">
          {calendarDates.map((date, index) => (
            <div
              key={index}
              className="ui-flex ui-w-full ui-min-w-0 ui-flex-1 ui-items-center ui-justify-center ui-text-center"
            >
              <InnerButton
                onClick={() => onToggleViewMode(index)}
                theme="solid"
                size="md"
                clickable={false}
                color="secondary"
                rightSlot={faAngleDown}
                defaultSizeIcon={false}
                iconSizeExtended="lg"
                colorIcon="blue"
                aria-label={`Cambiar vista a ${viewModes[index] === 'days' ? 'meses' : viewModes[index] === 'months' ? 'años' : 'días'}`}
              >
                {formatDate(date, viewModes[index])}
              </InnerButton>
            </div>
          ))}
        </div>

        {/* Botón derecho */}
        <NavigationButton direction="next" onClick={() => onNavigate('next')} />
      </div>

      {/* {onGoToToday && (
        <div className="ui-flex ui-justify-center ui-mt-2">
          <InnerButton
            variant="text"
            size="sm"
            onClick={onGoToToday}
 
          >
            Ir a hoy
          </InnerButton>
        </div>
      )} */}
    </div>
  );
};
